import React, { useState, useEffect } from 'react';
import './HomePage.css';
import bannerService, { BannerData } from '../services/bannerService';

interface CommonHeaderProps {
  showEditButton?: boolean;
  onEditClick?: () => void;
}

interface User {
  employeeName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  profilePicture?: string;
}

interface SearchResult {
  id: number;
  employee_name: string;
  designation: string;
  email: string;
  contact_no: string;
  ext_no: string;
}

const CommonHeader: React.FC<CommonHeaderProps> = ({ showEditButton = true, onEditClick }) => {
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [bannerData, setBannerData] = useState<BannerData | null>(null);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [displayedResults, setDisplayedResults] = useState<SearchResult[]>([]);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);

  useEffect(() => {
    loadUserProfile();
    loadBannerData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.search-container')) {
        setShowSearchDropdown(false);
      }
    };

    if (showSearchDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchDropdown]);

  const loadBannerData = async () => {
    try {
      const response = await bannerService.getBanner();
      if (response.success && response.data) {
        setBannerData(response.data);
      }
    } catch (error) {
      console.error('Error loading banner data:', error);
    }
  };

  const loadUserProfile = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const user = data.user || data;
        console.log('User profile loaded:', {
          email: user.email,
          hasProfilePicture: !!user.profilePicture,
          profilePictureLength: user.profilePicture?.length || 0,
          profilePictureStart: user.profilePicture?.substring(0, 50) || 'none'
        });
        setCurrentUser(user);
        setUserRole(user.roleName || user.role_name || '');
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const handleLogout = () => {
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('isLoggedIn');
    window.location.href = '/';
  };

  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Only search if query has at least 2 characters
    if (query.trim().length < 2) {
      setSearchResults([]);
      setDisplayedResults([]);
      setHasMoreResults(false);
      setCurrentOffset(0);
      return;
    }

    // Reset and perform search
    setCurrentOffset(0);
    await performSearch(query, 0);
  };

  const performSearch = async (query: string, offset: number) => {
    try {
      setIsSearching(true);
      const token = localStorage.getItem('jwtToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/users/search?q=${encodeURIComponent(query)}&limit=3&offset=${offset}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const newResults = data.users || [];
        
        if (offset === 0) {
          // Initial search
          setSearchResults(newResults);
          setDisplayedResults(newResults);
        } else {
          // Load more
          const combined = [...displayedResults, ...newResults];
          setDisplayedResults(combined);
        }
        
        setHasMoreResults(data.hasMore || false);
      } else {
        setSearchResults([]);
        setDisplayedResults([]);
        setHasMoreResults(false);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
      setDisplayedResults([]);
      setHasMoreResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleLoadMore = async () => {
    const newOffset = currentOffset + 3;
    setCurrentOffset(newOffset);
    await performSearch(searchQuery, newOffset);
  };

  const handleSearchFocus = () => {
    setShowSearchDropdown(true);
  };

  const handleSearchBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Don't close if clicking inside the search container (dropdown or buttons)
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget && relatedTarget.closest('.search-container')) {
      return;
    }
    // The click-outside handler will take care of closing
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setDisplayedResults([]);
    setHasMoreResults(false);
    setCurrentOffset(0);
    setShowSearchDropdown(false);
  };

  const getUserDisplayName = () => {
    if (currentUser?.employeeName) {
      return currentUser.employeeName;
    }
    if (currentUser?.firstName && currentUser?.lastName) {
      return `${currentUser.firstName} ${currentUser.lastName}`;
    }
    if (currentUser?.email) {
      return currentUser.email.split('@')[0];
    }
    return 'User';
  };

  const getProfilePicture = () => {
    // Check if user has a profile picture
    if (currentUser?.profilePicture) {
      const pic = currentUser.profilePicture;
      console.log('Getting profile picture:', {
        length: pic.length,
        isBase64: pic.startsWith('data:image'),
        start: pic.substring(0, 50)
      });
      
      // If it's already a base64 string, return as is
      if (pic.startsWith('data:image')) {
        return pic;
      }
      // If it's a URL, return as is
      if (pic.startsWith('http')) {
        return pic;
      }
      // If it's a path, return as is
      return pic;
    }
    // Default fallback image
    console.log('No profile picture, using default');
    return '/assets/images/logo.png';
  };

  return (
    <header className="main-header" style={{overflow: 'visible'}}>
      <div className="container" style={{overflow: 'visible'}}>
        <div className="header-content" style={{overflow: 'visible'}}>
          {/* Logo */}
          <div className="logo-container">
            <a href="/home" className="logo">
              <img src="/assets/images/jwil-logo.svg" alt="JWIL Logo" className="logo-img" />
            </a>
          </div>

          {/* Search Bar */}
          <div className="search-container">
            <div className="search-box" style={{ width: 'fit-content' }}>
              <img src="/assets/images/call.svg" alt="Call Icon" className="search-icon-img" />
              <input
                type="text"
                className="search-input"
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowSearchDropdown(true);
                }}
              />
              {searchQuery.length > 0 && (
                <button 
                  type="button" 
                  className="clear-search-btn visible"
                  onClick={clearSearch}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    background: '#e0e0e0',
                    border: 'none',
                    borderRadius: '50%',
                    width: '22px',
                    height: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '18px',
                    lineHeight: '1',
                    color: '#666',
                    padding: '0',
                    opacity: 1,
                    visibility: 'visible',
                    pointerEvents: 'auto'
                  }}
                >
                  ×
                </button>
              )}
              <div className="search-results-dropdown" style={{
                display: showSearchDropdown && searchQuery.length >= 2 ? 'block' : 'none',
                position: 'absolute',
                top: '100%',
                left: '0',
                right: '0',
                background: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                zIndex: 1000,
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                {isSearching && currentOffset === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    Searching...
                  </div>
                ) : displayedResults.length > 0 ? (
                  <>
                    <ul className="search-results-list">
                      {displayedResults.map((result) => (
                        <li key={result.id} className="search-result-item">
                          <div className="result-info">
                            <p className="result-name">{result.employee_name}</p>
                            <p className="result-title">{result.designation || 'N/A'}</p>
                            <p className="result-contact">{result.email} • {result.contact_no || 'N/A'}</p>
                          </div>
                          <div className="result-ext">{result.ext_no || '-'}</div>
                        </li>
                      ))}
                    </ul>
                    {hasMoreResults && (
                      <div style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        borderTop: '1px solid #e0e0e0'
                      }}>
                        <button
                          onClick={handleLoadMore}
                          disabled={isSearching}
                          style={{
                            background: 'none',
                            border: '1px solid #00a0e3',
                            color: '#00a0e3',
                            padding: '6px 16px',
                            borderRadius: '4px',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: isSearching ? 'not-allowed' : 'pointer',
                            opacity: isSearching ? 0.6 : 1,
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => !isSearching && (e.currentTarget.style.background = '#e3f2fd')}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                          {isSearching ? 'Loading...' : 'See More'}
                        </button>
                      </div>
                    )}
                  </>
                ) : searchQuery.length >= 2 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    No contacts found
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Ticker - Only show if banner exists and is visible */}
          {bannerData?.is_visible && (
            <div className="ticker-bar">
              <div className="ticker-content">
                <div className="ticker-text-wrapper">
                  <p className="ticker-text">{bannerData?.content || "Bring harmony to team expenses with budget limits and real-time monitoring."}</p>
                </div>
                <span className="icon-edit"></span>
              </div>
            </div>
          )}

          {/* User Profile */}
          <div className="user-profile" style={{overflow: 'visible'}}>
            <div className="user-greeting">
              <span>{getGreeting()}:</span>
              <p>{getUserDisplayName()}</p>
            </div>
            <div className="user-avatar" onClick={() => {
              setShowProfileDropdown(!showProfileDropdown);
            }}>
              <img 
                src={getProfilePicture()} 
                alt="User Avatar" 
                className="avatar-img"
                onError={(e) => {
                  console.error('Profile picture failed to load, using fallback');
                  const target = e.target as HTMLImageElement;
                  if (target.src !== '/assets/images/logo.png') {
                    target.src = '/assets/images/logo.png';
                  }
                }}
                onLoad={() => {
                  console.log('Profile picture loaded successfully');
                }}
              />
              {showProfileDropdown && (
                <div 
                  className="profile-dropdown-portal"
                  style={{
                    position: 'absolute',
                    top: '110%',
                    right: '-10px',
                    background: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    minWidth: '180px',
                    zIndex: 99999,
                    padding: '8px 0'
                  }}
                >
                  <div className="dropdown-arrow" style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '20px',
                    width: '0',
                    height: '0',
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderBottom: '8px solid white'
                  }}></div>
                  {showEditButton && userRole !== 'Employee' && (
                    <button className="dropdown-item" onClick={() => {
                      if (onEditClick) {
                        onEditClick();
                      } else {
                        window.location.href = '/edit-content';
                      }
                    }} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      fontSize: '14px',
                      color: '#333',
                      cursor: 'pointer'
                    }}>
                      <i className="icon-edit"></i>
                      Edit
                    </button>
                  )}
                  <button className="dropdown-item logout" onClick={handleLogout} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    fontSize: '14px',
                    color: '#dc3545',
                    cursor: 'pointer',
                    borderTop: (showEditButton && userRole !== 'Employee') ? '1px solid #e9ecef' : 'none'
                  }}>
                    <i className="icon-logout"></i>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default CommonHeader;
